import { useCallback, useRef, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

/** COMPONENT */
import { Box, MenuItem, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import InputTextField from "../../../component/input/inputTextField";
import FilterSelect from "../../../component/select/filterSelect";
import Loading from "../../../component/loading/loading";
import { notiError, notiSuccess } from "../../../component/notification/notifications";
import Cloudupload from "../../../assets/icon/cloudupload";

/** API */
import InheritanceTransferApi from "../../../api/inheritance-transfer/inheritance-transfer.api";

/** UTILS */
import { colors } from "../../../constants/colors";
import {
  INHERITANCE_REVIEW_ACCEPT,
  INHERITANCE_REVIEW_MAX_FILES,
  INHERITANCE_REVIEW_MAX_FILE_SIZE,
  INHERITANCE_REVIEW_MAX_NOTE_LENGTH,
  INHERITANCE_REVIEW_OPTIONS,
  inheritanceStatusColor,
  inheritanceStatusLabel,
} from "../../../constants/inheritanceTransfer";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/api-error.utils";
import { formatFileSize } from "../../../utils/common";

/** STYLE */
import * as Style from "../inheritanceTransfer.style";

type Props = {
  id: number;
  inheritanceNo?: string;
  ownerName?: string;
  heirName?: string;
  onClose: () => void;
  /** สำเร็จแล้ว: parent ปิด modal + reload ตาราง ในจังหวะเดียว */
  onSuccess: () => void;
};

export default function ModalInheritanceReview({
  id,
  inheritanceNo,
  ownerName,
  heirName,
  onClose,
  onSuccess,
}: Props) {
  const [result, setResult] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  /** กันกดปุ่มรัวก่อน isSubmitting จะ re-render ทัน */
  const isSubmittingRef = useRef<boolean>(false);

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const isTooBig = rejections.some((rejection) =>
      rejection.errors.some((error) => error.code === "file-too-large")
    );
    notiError(
      isTooBig
        ? `ไฟล์มีขนาดเกิน ${formatFileSize(INHERITANCE_REVIEW_MAX_FILE_SIZE)}`
        : "รับเฉพาะไฟล์ PDF, JPG หรือ PNG"
    );
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles?.length) return;

      const nextFiles = [...files];
      let duplicated = 0;
      let overflow = 0;

      acceptedFiles.forEach((acceptedFile) => {
        if (nextFiles.length >= INHERITANCE_REVIEW_MAX_FILES) {
          overflow += 1;
          return;
        }
        // เทียบ name + size + lastModified เพราะ path ไม่ได้มีทุก browser
        const isDuplicated = nextFiles.some(
          (x) =>
            x.name === acceptedFile.name &&
            x.size === acceptedFile.size &&
            x.lastModified === acceptedFile.lastModified
        );
        if (isDuplicated) {
          duplicated += 1;
          return;
        }
        nextFiles.push(acceptedFile);
      });

      if (duplicated) notiError("ไฟล์นี้ถูกแนบไว้แล้ว");
      if (overflow) notiError(`แนบไฟล์ได้ไม่เกิน ${INHERITANCE_REVIEW_MAX_FILES} ไฟล์`);
      setFiles(nextFiles);
    },
    [files]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: INHERITANCE_REVIEW_ACCEPT,
    multiple: true,
    maxSize: INHERITANCE_REVIEW_MAX_FILE_SIZE,
    disabled: isSubmitting,
  });

  /** ลบด้วย index ไม่ใช่ path — path เป็น undefined ได้ทุกไฟล์ ซึ่งจะทำให้ filter ลบยกลิสต์ */
  const handleRemoveFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const onSubmit = async () => {
    if (!result || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      // endpoint นี้รับ multipart ตรงๆ ไม่ใช่ 2-step แบบ upload อื่นในระบบ
      // ไม่ set Content-Type เอง ต้องให้ browser ใส่ boundary ของ multipart
      const body = new FormData();
      body.append("result", result);
      if (note.trim()) body.append("note", note.trim());
      files.forEach((file) => body.append("files", file));

      await InheritanceTransferApi.review(id, body);
      notiSuccess("บันทึกผลการพิจารณาสำเร็จ");
      onSuccess();
    } catch (error) {
      const status = getApiErrorStatus(error);
      const message = getApiErrorMessage(error, "บันทึกผลการพิจารณาไม่สำเร็จ");
      notiError(message);
      // 404 หรือ "ถูกพิจารณาไปแล้ว" = ข้อมูลบนหน้าจอไม่ตรงกับ DB แล้ว ปิดแล้วโหลดใหม่ให้เห็นค่าจริง
      if (status === 404 || message.includes("พิจารณาไปแล้ว")) onSuccess();
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const subTitle = [inheritanceNo, ownerName && heirName && `${ownerName} → ${heirName}`]
    .filter(Boolean)
    .join(" · ");

  const renderSelectedFiles = () => (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 14, color: colors.textLightGray, mb: 1 }}>
        {`${files.length}/${INHERITANCE_REVIEW_MAX_FILES} ไฟล์`}
      </Typography>
      {files.map((file, index) => (
        <Style.FileBox key={`${file.name}-${file.lastModified}-${index}`} sx={{ mt: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: 16, wordBreak: "break-all" }}>{file.name}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
                {formatFileSize(file.size)}
              </Typography>
              <DeleteIcon
                color="error"
                sx={{ cursor: isSubmitting ? "not-allowed" : "pointer" }}
                onClick={() => {
                  if (!isSubmitting) handleRemoveFile(index);
                }}
              />
            </Box>
          </Box>
        </Style.FileBox>
      ))}
    </Box>
  );

  const renderBody = () => (
    <Box
      className="custom-scroll"
      sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}
    >
      {subTitle && (
        <Typography sx={{ fontSize: 16, color: colors.textLightGray, textAlign: "center", mb: 3 }}>
          {subTitle}
        </Typography>
      )}

      <Box sx={{ mb: 3 }}>
        <FilterSelect
          labelId="inheritance-review-result"
          selectId="inheritance-review-result"
          heading="ผลการพิจารณา"
          required
          value={result}
          disabled={isSubmitting}
          onchange={(event: any) => setResult(event.target.value)}
          renderValue={() =>
            result ? (
              inheritanceStatusLabel(result)
            ) : (
              <span style={{ color: colors.black_60 }}>เลือกผลการพิจารณา</span>
            )
          }
          options={INHERITANCE_REVIEW_OPTIONS.map((option: string) => (
            <MenuItem key={option} value={option}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: inheritanceStatusColor(option),
                  }}
                />
                {inheritanceStatusLabel(option)}
              </Box>
            </MenuItem>
          ))}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <InputTextField
          heading="รายละเอียดการพิจารณา"
          value={note}
          multiline
          rows={4}
          maxRows={4}
          // multiline ต้องคู่กับ inputHeight 'auto' ไม่งั้นกล่องถูกล็อกความสูง 43px
          inputHeight={"auto"}
          disabled={isSubmitting}
          placeholder="ระบุเหตุผลหรือรายละเอียดเพิ่มเติม"
          inputProps={{ maxLength: INHERITANCE_REVIEW_MAX_NOTE_LENGTH }}
          onchange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
        />
      </Box>

      <Typography sx={{ fontSize: 16, mb: 1 }}>แนบไฟล์เอกสาร</Typography>
      <Box
        {...getRootProps()}
        sx={{
          width: "100%",
          border: `2px dashed ${colors.bgText}`,
          borderRadius: "8px",
          py: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: isSubmitting ? "not-allowed" : "pointer",
          backgroundColor: isDragActive ? colors.extraLightGray : colors.white,
        }}
      >
        <input {...getInputProps()} />
        <Cloudupload />
        <Typography sx={{ mt: 1, fontSize: 16 }}>เลือกไฟล์ หรือลากไฟล์มาวาง</Typography>
        <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
          {`PDF, JPG, PNG ขนาดไม่เกิน ${formatFileSize(INHERITANCE_REVIEW_MAX_FILE_SIZE)}`}
        </Typography>
      </Box>
      {files.length > 0 && renderSelectedFiles()}
    </Box>
  );

  return (
    <>
      {isSubmitting && <Loading show />}
      <ModalCustom
        // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
        isShowProps
        // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
        autoResetModal={false}
        size="xl"
        closeButton
        alignFooter="end"
        footerInline
        title="พิจารณาคำร้องโอนมรดก"
        component={renderBody()}
        textBtnCancel="ยกเลิก"
        onReset={onClose}
        textBtnConfirm="ยืนยัน"
        onSubmit={onSubmit}
        
        disabledSubmit={!result || isSubmitting}
        onClose={onClose}
      />
    </>
  );
}
